import { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Layout, LayoutContent, LayoutFooter } from '@astryxdesign/core/Layout'
import { Stack, StackItem } from '@astryxdesign/core/Stack'
import { Heading } from '@astryxdesign/core/Heading'
import { Text } from '@astryxdesign/core/Text'
import { Button } from '@astryxdesign/core/Button'
import { Banner } from '@astryxdesign/core/Banner'
import { Spinner } from '@astryxdesign/core/Spinner'
import { TextInput } from '@astryxdesign/core/TextInput'
import { Dialog, DialogHeader } from '@astryxdesign/core/Dialog'
import { RadioList, RadioListItem } from '@astryxdesign/core/RadioList'
import { List, ListItem } from '@astryxdesign/core/List'
import { createCourtGame, listMembers, searchPlayers } from './consoleApi'
import {
    GUEST_NAME_MAX,
    PLACE_KINDS,
    chooseOrganiser,
    createGameDoneText,
    createGameFailureText,
    emptyPlaces,
    gameDraftError,
    gameToCreate,
    isPlaceFilled,
    memberName,
    memberPlace,
    organiserChoices,
    pickableMembers,
    placeDescription,
    playerLine,
    playerPlace,
} from './consoleText'

// "Créer une partie…": one free slot, up to four players, one of them the
// organiser. Each place is a member of the club's roster, a Padelr player, or
// a guest typed in by name; the server decides how a member without an
// account plays (as a guest named after them) and holds the court.
//
// Searches go to the server a moment after the admin stops typing, the way
// the Adhérents tab does it.
const SEARCH_DELAY_MS = 300
const MAX_RESULTS = 8

export default function CreateGameDialog({ isOpen, clubId, date, cell, where, onClose, onCreated, onFailure, onSessionLost }) {
    const [places, setPlaces] = useState(emptyPlaces)
    const [organiser, setOrganiser] = useState('')
    const [isCreating, setCreating] = useState(false)

    // Every dialog starts empty: the players of the previous game are not
    // carried to the next slot.
    useEffect(() => {
        if (isOpen) {
            setPlaces(emptyPlaces())
            setOrganiser('')
        }
    }, [isOpen])

    const setPlace = (index, place) => {
        const next = places.map((p, i) => (i === index ? place : p))
        setPlaces(next)
        setOrganiser(current => chooseOrganiser(next, current))
    }

    const error = gameDraftError(places, organiser)
    const choices = organiserChoices(places)
    const hasPlayers = places.some(isPlaceFilled)

    const confirm = async () => {
        if (error || !cell) return
        setCreating(true)
        let answer
        try {
            answer = await createCourtGame(clubId, date, cell, gameToCreate(places, organiser))
        } catch (e) {
            answer = { status: 0 }
        } finally {
            setCreating(false)
        }
        if (answer.status === 200 && answer.json) {
            onCreated(createGameDoneText(Number(answer.json.notified) || 0))
        } else if (answer.status === 401) {
            onSessionLost()
        } else {
            onFailure(answer.status, createGameFailureText(answer.status))
        }
    }

    // Who is already in the game, so a search does not offer them twice.
    const taken = {
        members: new Set(places.filter(isPlaceFilled).map(p => p.clubUserId).filter(Boolean)),
        users: new Set(places.filter(isPlaceFilled).map(p => p.userId).filter(Boolean)),
    }

    return (
        <Dialog isOpen={isOpen} onOpenChange={open => !open && onClose()} purpose="form" width={560}>
            <Layout
                header={<DialogHeader title="Créer une partie" subtitle={where} onOpenChange={() => onClose()} />}
                content={
                    <LayoutContent>
                        <Stack gap={5}>
                            {places.map((place, index) => (
                                <PlaceEditor
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={index}
                                    index={index}
                                    place={place}
                                    clubId={clubId}
                                    taken={taken}
                                    onChange={next => setPlace(index, next)}
                                    onSessionLost={onSessionLost}
                                />
                            ))}
                            {choices.length > 0 && (
                                <RadioList label="Organisateur" isRequired value={organiser} onChange={setOrganiser}
                                    description="Il gère la partie dans l'app Padelr, comme une partie qu'il aurait créée.">
                                    {choices.map(c => <RadioListItem key={c.userId} value={c.userId} label={c.name} />)}
                                </RadioList>
                            )}
                            {hasPlayers && error && <Text type="supporting" color="secondary">{error}</Text>}
                        </Stack>
                    </LayoutContent>
                }
                footer={
                    <LayoutFooter>
                        <Stack direction="horizontal" gap={2} hAlign="end">
                            <Button label="Annuler" variant="secondary" onClick={onClose} />
                            <Button
                                label="Créer la partie"
                                variant="primary"
                                isDisabled={!!error || !cell}
                                isLoading={isCreating}
                                onClick={confirm}
                            />
                        </Stack>
                    </LayoutFooter>
                }
            />
        </Dialog>
    )
}

CreateGameDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    clubId: PropTypes.string.isRequired,
    date: PropTypes.object.isRequired,
    cell: PropTypes.shape({
        court_id: PropTypes.string.isRequired,
        start_time: PropTypes.string.isRequired,
    }),
    // "Terrain 1 · Central — samedi 24 octobre à 18:30".
    where: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
    onCreated: PropTypes.func.isRequired,
    onFailure: PropTypes.func.isRequired,
    onSessionLost: PropTypes.func.isRequired,
}

// One place: once filled, who is in it and a way to take them out; before,
// what kind of player it takes and the way to find them.
function PlaceEditor({ index, place, clubId, taken, onChange, onSessionLost }) {
    const title = `Joueur ${index + 1}`
    const clear = () => onChange({ ...emptyPlaces()[0], kind: place.kind })

    if (place.kind !== 'guest' && isPlaceFilled(place)) {
        return (
            <Stack gap={2}>
                <Heading level={3}>{title}</Heading>
                <Stack direction="horizontal" gap={2} vAlign="center">
                    <StackItem size="fill">
                        <Stack gap={0.5}>
                            <Text weight="semibold">{place.name}</Text>
                            <Text size="sm" color="secondary">{placeDescription(place)}</Text>
                        </Stack>
                    </StackItem>
                    <Button label="Retirer" variant="ghost" onClick={clear} />
                </Stack>
            </Stack>
        )
    }

    return (
        <Stack gap={2}>
            <Heading level={3}>{title}</Heading>
            <RadioList
                label={`Type du joueur ${index + 1}`}
                isLabelHidden
                orientation="horizontal"
                value={place.kind}
                onChange={kind => onChange({ ...emptyPlaces()[0], kind })}
            >
                {PLACE_KINDS.map(k => <RadioListItem key={k.value} value={k.value} label={k.label} />)}
            </RadioList>
            {place.kind === 'member' && (
                <Search
                    label="Adhérent"
                    placeholder="Nom de l'adhérent"
                    find={async query => {
                        const answer = await listMembers(clubId, query)
                        return answer.status === 200 && answer.json
                            ? { status: 200, list: pickableMembers(answer.json.list || []) }
                            : { status: answer.status }
                    }}
                    toItem={m => ({
                        key: m.id,
                        label: memberName(m),
                        description: m.user_id ? 'Compte Padelr' : 'Sans compte Padelr',
                        isDisabled: taken.members.has(m.id) || (!!m.user_id && taken.users.has(m.user_id)),
                        onPick: () => onChange(memberPlace(m)),
                    })}
                    emptyText="Aucun adhérent ne correspond."
                    onSessionLost={onSessionLost}
                />
            )}
            {place.kind === 'player' && (
                <Search
                    label="Joueur Padelr"
                    placeholder="Nom ou pseudo"
                    find={async query => {
                        const answer = await searchPlayers(clubId, query)
                        return answer.status === 200 && answer.json
                            ? { status: 200, list: answer.json.list || [] }
                            : { status: answer.status }
                    }}
                    toItem={p => ({
                        key: p.id,
                        label: p.full_name || `@${p.username}`,
                        description: playerLine(p) || undefined,
                        isDisabled: taken.users.has(p.id),
                        onPick: () => onChange(playerPlace(p)),
                    })}
                    emptyText="Aucun joueur ne correspond. Ajoutez-le comme invité."
                    onSessionLost={onSessionLost}
                />
            )}
            {place.kind === 'guest' && (
                <TextInput
                    label="Nom de l'invité"
                    value={place.name}
                    onChange={name => onChange({ ...place, name: name.slice(0, GUEST_NAME_MAX) })}
                    placeholder="ex. Thomas"
                    hasAutoFocus
                    width="100%"
                />
            )}
        </Stack>
    )
}

PlaceEditor.propTypes = {
    index: PropTypes.number.isRequired,
    place: PropTypes.object.isRequired,
    clubId: PropTypes.string.isRequired,
    taken: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    onSessionLost: PropTypes.func.isRequired,
}

// A search box and its results. Only the answer to the latest query is kept.
// Also the intervenants' picker of a block (AssigneePicker), which says
// something else of somebody already picked.
export function Search({ label, placeholder, find, toItem, emptyText, takenText = 'Déjà dans la partie', hasAutoFocus = true, onSessionLost }) {
    const [search, setSearch] = useState('')
    const [found, setFound] = useState({ status: 'idle', list: [] })
    const latest = useRef(0)
    const findRef = useRef(find)
    findRef.current = find

    useEffect(() => {
        const query = search.trim()
        if (!query) {
            latest.current++
            setFound({ status: 'idle', list: [] })
            return undefined
        }
        const timer = setTimeout(async () => {
            const call = ++latest.current
            setFound(current => ({ ...current, status: 'loading' }))
            let answer
            try {
                answer = await findRef.current(query)
            } catch (e) {
                answer = { status: 0 }
            }
            if (call !== latest.current) return
            if (answer.status === 200) {
                setFound({ status: 'ready', list: answer.list.slice(0, MAX_RESULTS) })
            } else if (answer.status === 401) {
                onSessionLost()
            } else {
                setFound({ status: 'error', list: [] })
            }
        }, SEARCH_DELAY_MS)
        return () => clearTimeout(timer)
    }, [search, onSessionLost])

    const items = found.list.map(toItem)

    return (
        <Stack gap={2}>
            <TextInput
                label={label}
                isLabelHidden
                startIcon="search"
                placeholder={placeholder}
                value={search}
                onChange={setSearch}
                autoComplete="off"
                hasAutoFocus={hasAutoFocus}
                width="100%"
            />
            {found.status === 'loading' && items.length === 0 && <Spinner size="sm" label="Recherche" />}
            {found.status === 'error' && <Banner status="error" title="La recherche a échoué. Réessayez." />}
            {found.status === 'ready' && items.length === 0 && (
                <Text type="supporting" color="secondary">{emptyText}</Text>
            )}
            {items.length > 0 && (
                <List hasDividers density="compact">
                    {items.map(item => (
                        <ListItem
                            key={item.key}
                            label={item.label}
                            description={item.isDisabled ? takenText : item.description}
                            isDisabled={item.isDisabled}
                            onClick={item.isDisabled ? undefined : item.onPick}
                        />
                    ))}
                </List>
            )}
        </Stack>
    )
}

Search.propTypes = {
    label: PropTypes.string.isRequired,
    placeholder: PropTypes.string.isRequired,
    find: PropTypes.func.isRequired,
    toItem: PropTypes.func.isRequired,
    emptyText: PropTypes.string.isRequired,
    takenText: PropTypes.string,
    hasAutoFocus: PropTypes.bool,
    onSessionLost: PropTypes.func.isRequired,
}
